import {
  HomeViewportProps,
  HomeVisitEndReason,
  logHomeTileDwell,
  logHomeTileSeen,
  logHomeVisitSummary,
} from './dashboardAnalytics';
import {TileId} from './dashboardModel';
import {TileContentStatus} from './tileCatalog';

/** A tile must stay on screen this long to count as seen. */
export const TILE_SEEN_DWELL_MS = 1000;
/** Scroll smaller than this (layout nudges, restoration) still counts as above the fold. */
const SCROLL_SLOP_PX = 8;

interface TileVisit {
  intersecting: boolean;
  tileTopPx: number;
  visibleSince?: number;
  visibleMs: number;
  seenTimer?: ReturnType<typeof setTimeout>;
  seen: boolean;
  aboveFold: boolean;
}

/**
 * One home visit: turns tile visibility, scroll, and tab focus into
 * HOME_TILE_SEEN as tiles are seen, then HOME_TILE_DWELL and
 * HOME_VISIT_SUMMARY when the visit ends.
 */
export class HomeVisitTracker {
  private readonly startedAt = Date.now();
  private tiles = new Map<TileId, TileVisit>();
  private order: TileId[] = [];
  private statuses: Partial<Record<TileId, TileContentStatus>> = {};
  private viewport: HomeViewportProps = {viewportWidth: 0, viewportHeight: 0, effectiveColumns: 0};
  private pageVisible = true;
  private editing = false;
  private activeSince?: number;
  private activeMs = 0;
  private scrollPx = 0;
  private maxScrollPx = 0;
  private maxSeenPct = 0;
  private dashboardHeightPx = 0;
  private ended = false;

  constructor(
    readonly visitId: string,
    {pageVisible}: {pageVisible: boolean}
  ) {
    this.pageVisible = pageVisible;
    this.syncActive();
  }

  setOrder(order: TileId[]) {
    this.order = order;
  }

  setContentStatuses(statuses: Partial<Record<TileId, TileContentStatus>>) {
    this.statuses = statuses;
  }

  setViewport(viewport: HomeViewportProps) {
    this.viewport = viewport;
  }

  setPageVisible(visible: boolean) {
    this.pageVisible = visible;
    this.syncActive();
  }

  /** Customize mode doesn't count: tiles there are being arranged, not read. */
  setEditing(editing: boolean) {
    this.editing = editing;
    this.syncActive();
  }

  setScroll(scrollPx: number, seenPct: number, dashboardHeightPx: number) {
    this.scrollPx = Math.max(0, scrollPx);
    this.maxScrollPx = Math.max(this.maxScrollPx, this.scrollPx);
    this.maxSeenPct = Math.max(this.maxSeenPct, Math.round(Math.min(1, seenPct) * 100));
    this.dashboardHeightPx = dashboardHeightPx;
  }

  /** `intersecting` here means visible enough to count, not just touching the screen. */
  setTileVisibility(tileId: TileId, intersecting: boolean, tileTopPx?: number) {
    const tile = this.tiles.get(tileId) ?? {
      intersecting: false,
      tileTopPx: 0,
      visibleMs: 0,
      seen: false,
      aboveFold: false,
    };
    tile.intersecting = intersecting;
    tile.tileTopPx = tileTopPx ?? tile.tileTopPx;
    if (intersecting && this.maxScrollPx <= SCROLL_SLOP_PX) {
      tile.aboveFold = true;
    }
    this.tiles.set(tileId, tile);
    this.syncTile(tileId);
  }

  end(endReason: HomeVisitEndReason) {
    if (this.ended) {
      return;
    }
    this.ended = true;
    this.syncActive();

    const tiles = [...this.tiles.entries()];
    const seen = tiles.filter(([, tile]) => tile.seen);
    seen.forEach(([tileId, tile]) => {
      logHomeTileDwell({
        visitId: this.visitId,
        tileId,
        position: this.positionOf(tileId),
        aboveFold: tile.aboveFold,
        visibleMs: tile.visibleMs,
        ...this.viewport,
      });
    });

    logHomeVisitSummary({
      visitId: this.visitId,
      endReason,
      durationMs: this.activeMs,
      maxScrollPx: this.maxScrollPx,
      maxSeenPct: this.maxSeenPct,
      dashboardHeightPx: this.dashboardHeightPx,
      tileCount: this.tiles.size,
      seenCount: seen.length,
      tilesRendered: [...this.tiles.keys()],
      tilesSeen: seen.map(([tileId]) => tileId),
      tilesAboveFold: tiles.filter(([, tile]) => tile.aboveFold).map(([tileId]) => tileId),
      ...this.viewport,
    });
  }

  private get active() {
    return this.pageVisible && !this.editing && !this.ended;
  }

  private syncActive() {
    const now = Date.now();
    if (this.active && this.activeSince === undefined) {
      this.activeSince = now;
    } else if (!this.active && this.activeSince !== undefined) {
      this.activeMs += now - this.activeSince;
      this.activeSince = undefined;
    }
    this.tiles.forEach((_, tileId) => this.syncTile(tileId));
  }

  private syncTile(tileId: TileId) {
    const tile = this.tiles.get(tileId);
    if (!tile) {
      return;
    }
    const visible = tile.intersecting && this.active;
    const now = Date.now();
    if (visible && tile.visibleSince === undefined) {
      tile.visibleSince = now;
      if (!tile.seen) {
        tile.seenTimer = setTimeout(() => this.markSeen(tileId), TILE_SEEN_DWELL_MS);
      }
    } else if (!visible && tile.visibleSince !== undefined) {
      tile.visibleMs += now - tile.visibleSince;
      tile.visibleSince = undefined;
      clearTimeout(tile.seenTimer);
      tile.seenTimer = undefined;
    }
  }

  private markSeen(tileId: TileId) {
    const tile = this.tiles.get(tileId);
    if (!tile) {
      return;
    }
    tile.seen = true;
    tile.seenTimer = undefined;
    logHomeTileSeen({
      visitId: this.visitId,
      tileId,
      position: this.positionOf(tileId),
      aboveFold: tile.aboveFold,
      msToSeen: Date.now() - this.startedAt,
      scrollPx: Math.round(this.scrollPx),
      tileTopPx: Math.round(tile.tileTopPx),
      contentStatus: this.statuses[tileId],
      ...this.viewport,
    });
  }

  private positionOf(tileId: TileId) {
    return this.order.indexOf(tileId) + 1;
  }
}
